import React, { useEffect, useRef, useState } from 'react';

import { Badge } from 'antd';
import { FaStar } from 'react-icons/fa6';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch } from 'react-redux';

import CourseAPI from '../../api/CourseAPI';
import FilterButtonWithBottomSheet from '../../components/Header/components/FilterButtonWithBottomSheet';
import SearchBar from '../../components/Header/SearchBar';
import Loader from '../../components/Loader';
import { setCourseTab } from '../../context/appReducer';
import { useTheme } from '../../context/ThemeProvider';
import { COURSES_PAGE_LIMIT } from '../../shared/constants';
import showAppError from '../../shared/error';
import { useAppNavigate } from '../../shared/hooks';
import { hasFeatureAccess } from '../../shared/utils';
import { FEATURES } from '../../types/baseTypes';
import { ICoursesState } from '../../types/courseTypes';
import { ROUTES } from '../../types/routes';
import CourseListCard from './CourseListCard/CourseListCard';
import NoCoursesContent from './NoCoursesContent';

const initialState: ICoursesState = {
  loading: false,
  hasMore: true,
  courses: [],
  page: 1,
  limit: COURSES_PAGE_LIMIT,
};

interface Props {
  userId: string | null;
}

const MyCourses: React.FC<Props> = ({ userId }) => {
  const navigate = useAppNavigate();
  const dispatch = useDispatch();

  const { colors } = useTheme();

  const [state, setState] = useState<ICoursesState>(initialState);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<{
    [key: string]: string;
  }>({ _id: 'course', title: 'Course' });

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const loadCourses = async (
    page: number = 1,
    currState: ICoursesState = state,
  ) => {
    if (!userId || currState.loading || !currState.hasMore) return;

    // if (state.courses?.length !== 0 && !searchQuery  ) {
    //   return;
    // }

    const errorMsg = 'Failed to load courses.';

    const newState = { ...initialState, ...currState };

    if (page === 1) {
      newState.courses = [];
      newState.page = 1;
      newState.hasMore = true;
    }

    newState.loading = true;

    setState({ ...newState });

    try {
      const resp = await CourseAPI.getCourses({
        page,
        limit: newState.limit,
        creator: userId,
        search: searchQuery,
        searchBy: selectedFilter._id,
      });
      if (resp.status === 200) {
        const newCourses = [...resp.data.result];

        newState.courses = [...newState.courses, ...newCourses];

        newState.hasMore = newCourses.length >= newState.limit;

        if (newState.hasMore) {
          newState.page += 1;
        }
      } else {
        showAppError(resp.data, errorMsg);
      }
    } catch (error: any) {
      showAppError(error, errorMsg);
    } finally {
      newState.loading = false;
      setState({ ...newState });
    }
  };

  useEffect(() => {
    loadCourses(1, initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, userId, selectedFilter]);

  useEffect(() => {
    dispatch(setCourseTab('my'));
    const element = document.getElementById('courses__content');

    if (element) {
      scrollContainerRef.current = element;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // console.log(state);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#fff',
          width: '100%',
        }}>
        <SearchBar
          onDebounce={(value) => {
            setSearchQuery(value);
          }}
          placeholder="Search"
          style={{
            width: '100%',
            borderBottomWidth: 1,
            borderColor: colors.BORDER,
          }}
        />
        <FilterButtonWithBottomSheet
          dateList={[
            { _id: 'course', title: 'Course' },
            { _id: 'section', title: 'Section' },
            { _id: 'chapter', title: 'Chapter' },
          ]}
          buttonTitle={selectedFilter?.title}
          searchKey="title"
          customHeaderTitle={`Search by`}
          customTitle={``}
          customDescription={''}
          noDataFoundTitle={``}
          filteredDataList={[selectedFilter]}
          setFilteredDataAction={(selectedData) => {
            setSelectedFilter(selectedData[0]);
          }}
          allowClear={false}
        />
      </div>

      <div className="courses__content">
        <InfiniteScroll
          hasMore={state.hasMore}
          loadMore={() => loadCourses(state.page)}
          useWindow={false}
          getScrollParent={() => scrollContainerRef.current as HTMLElement}
          loader={
            (state.loading || !userId || state.hasMore ? (
              <Loader
                style={{
                  margin: '20px 0',
                  width: '100%',
                }}
              />
            ) : undefined) as JSX.Element
          }>
          {state.courses.length > 0
            ? state.courses.map((item) => {
                if (
                  item?.isRecommendationEnabled &&
                  hasFeatureAccess(FEATURES.COURSE_RECOMMENDATION)
                ) {
                  return (
                    <Badge.Ribbon
                      key={item._id}
                      color={colors.PRIMARY}
                      text={
                        item?.isRecommendationEnabled ? (
                          <div className="course-card-recommended-badge">
                            <FaStar />
                            {` `}
                            Recommended
                          </div>
                        ) : null
                      }>
                      <CourseListCard
                        key={item._id}
                        onPress={() => {
                          navigate(ROUTES.COURSE_OVERVIEW, {
                            courseId: item._id,
                          });
                        }}
                        type="my"
                        title={item.title}
                        coverImage={item.coverImage}
                        mangoes={item.mangoArr}
                        courseCategory={item.category}
                        rawCourseData={item}
                      />
                    </Badge.Ribbon>
                  );
                }
                return (
                  <CourseListCard
                    key={item._id}
                    onPress={() => {
                      navigate(ROUTES.COURSE_OVERVIEW, {
                        courseId: item._id,
                      });
                    }}
                    type="my"
                    title={item.title}
                    coverImage={item.coverImage}
                    mangoes={item.mangoArr}
                    rawCourseData={item}
                  />
                );
              })
            : null}
        </InfiniteScroll>

        <NoCoursesContent userId={userId} state={state} />
      </div>
    </>
  );
};

export default MyCourses;
